import useIsBrowser from 'hooks/useIsBrowser';
import type {
  AdPlacementConfig,
  DisplayAdSlotFunction,
  GetPlacementConfigFunction,
  PageAdConfig,
  PlacementName,
  RegisterAdSlotRenderCallbackFunction,
} from 'interfaces/ads/Ad';
import { UserContext } from 'providers/UserProvider';
import React, { useContext, useMemo } from 'react';
import AdLibrary from './AdLibrary';
import { calculateBrowserWidth } from './utils/browser';
import buildConfig from './utils/buildConfig';
import { createAdTagPath } from './utils/createPath';
import usePageview from './utils/usePageView';

export interface AdContextInterface {
  adsEnabled?: boolean;
  initialized: boolean;
  channel: string;
  cid: string | null;
  pageAdConfig?: PageAdConfig;
  topics: string[];
  tags: string[];
  ctype: string;
  premiumCategory: string;
  getPlacementConfig: GetPlacementConfigFunction;
  displayAdSlot: DisplayAdSlotFunction;
  registerAdSlotRenderCallback: RegisterAdSlotRenderCallbackFunction;
  setRenderOutbrainScript?: React.Dispatch<React.SetStateAction<boolean>>;
  outbrainScriptLoaded?: boolean;
}

const emptyPlacementConfig: AdPlacementConfig = {
  lazyLoad: false,
  name: 'Footer',
  path: '',
  refreshRate: 60,
  sizeMappings: [],
};

export const defaultAdContext = {
  adsEnabled: false,
  channel: '',
  cid: '',
  ctype: '',
  getPlacementConfig: (): AdPlacementConfig => emptyPlacementConfig,
  initialized: false,
  premiumCategory: '',
  tags: [],
  topics: [],
  displayAdSlot: () => {},
  registerAdSlotRenderCallback: () => {},
};

export const AdContext = React.createContext<AdContextInterface>(defaultAdContext);

export const useAdContext = (
  pageAdConfig: PageAdConfig,
): {
  adsEnabled: boolean;
  channel: string;
  cid: string;
  ctype: string;
  getPlacementConfig: (name: PlacementName) => AdPlacementConfig;
  initialized: boolean;
  premiumCategory: string;
  tags: string[];
  topics: string[];
} => {
  const { hasConsent, personalizedAds } = useContext(UserContext);
  const isBrowser = useIsBrowser();
  const pv = usePageview(hasConsent);
  const pagesToUpdateDefaultPath = ['fortune.well', 'fortune.europe', 'fortune.asia', 'fortune.recommends'];

  if (isBrowser && calculateBrowserWidth() < 768) {
    if (pagesToUpdateDefaultPath.includes(pageAdConfig.defaultPath))
      pageAdConfig.defaultPath = `${pageAdConfig.defaultPath}/mobile`;
    else if (pageAdConfig.defaultPath === 'fortune') pageAdConfig.defaultPath = 'fortune/mobile';
  } else if (isBrowser && calculateBrowserWidth() >= 768) {
    if (pagesToUpdateDefaultPath.includes(pageAdConfig.defaultPath))
      pageAdConfig.defaultPath = `${pageAdConfig.defaultPath}/desktop`;
    else if (pageAdConfig.defaultPath === 'fortune') pageAdConfig.defaultPath = 'fortune/desktop';
  }

  if (pageAdConfig.ch === 'homepage') pageAdConfig.ch = 'home';
  const config = buildConfig(pageAdConfig, pv, !!personalizedAds);
  const adLib = AdLibrary.getInstance();
  (async () => {
    await adLib.init(config);
  })();

  return useMemo(() => {
    return {
      adsEnabled: true,
      channel: pageAdConfig.ch,
      cid: pageAdConfig.cid,
      ctype: pageAdConfig.ctype,
      getPlacementConfig: (name: PlacementName) => {
        const adLib = AdLibrary.getInstance();
        return adLib.getPlacementConfig(name);
      },

      initialized: true,
      premiumCategory: pageAdConfig.premiumCategory || '',
      tags: pageAdConfig.tags,
      topics: pageAdConfig.topics,
    };
  }, [pageAdConfig]);
};

export const useAdTag = (pathname: string): string => {
  const { personalizedAds } = useContext(UserContext);
  const { channel, cid, topics, tags, ctype, premiumCategory } = useContext(AdContext);

  const adTagPath = createAdTagPath(channel, pathname);
  return (
    'pubads.g.doubleclick.net/gampad/ads' +
    `?iu=/21809533738/${adTagPath}` +
    `&description_url=${encodeURIComponent(pathname)}` +
    '&env=vp' +
    '&impl=s' +
    '&correlator=' +
    '&tfcd=0' +
    `&npa=${personalizedAds ? '0' : '1'}` +
    '&gdfp_req=1' +
    '&output=vast' +
    '&sz=640x360' +
    '&ciu_szs=300x250,728x90' +
    '&unviewed_position_start=1' +
    '&cust_params=' +
    `cid=${cid}` +
    `%26topics=${encodeURIComponent(topics?.join(',') || '')}` +
    `%26tags=${encodeURIComponent(tags?.join(',') || '')}` +
    `%26ctype=${ctype}` +
    `%26premiumCategory=${premiumCategory}`
  );
};
